import {
  ApiException,
  ApprovalProcessConfigInputModel,
  ApprovalProcessConfigViewModel,
  ApprovalProcessViewModel,
  ApproverViewModel,
  FileResponse,
  FuelCardInputModel,
  FuelCardViewModel,
  NextApprovalStepInputModel,
  OrderInputModel,
  OrderInternalAuditViewModel,
  OrderInternalInputModel,
  OrderInternalViewModel,
  OrdersApi,
  OrdersFormAnonymousViewModel,
  OrdersFormInputModel,
  OrdersFormViewModel,
  OrderSuppliersAuditViewModel,
  OrderSuppliersInputModel,
  OrderSuppliersViewModel,
  OrderViewModel,
  SupplierUploadInputModel,
  SupplierUploadViewModel,
  TenantFuelCardSetInputModel,
  TenantFuelCardSetViewModel,
  VehicleOrderInputModel,
  VehicleOrderViewModel,
  VehicleReturnInputModel,
  VehicleReturnViewModel,
  WorkflowConfigInputModel,
  WorkflowConfigViewModel,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/userManager'
import { Constants } from '../../helpers/Constants'
import { getBaseQuery } from './apiQuery'

const client = new OrdersApi({
  baseUrl: Constants.ordersEndpoint,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

// prettier-ignore
export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: getBaseQuery<ApiException>(),
  endpoints: (builder) => ({
    getOrderSuppliers: builder.query<OrderSuppliersViewModel, { id: string; document?: string }>({
      query: (model) => client.getOrderSuppliers(model.id, model.document),
      providesTags: ['OrdersSuppliers'],
    }),
    getApprovalProcessConfigs: builder.query<ApprovalProcessConfigViewModel[], void>({
      query: () => client.getApprovalProcessConfigs(),
    }),
    postApprovalProcessConfig: builder.mutation<
      ApprovalProcessConfigViewModel,
      { force?: boolean; model: ApprovalProcessConfigInputModel }
    >({
      query: (rq) => client.postApprovalProcessConfig(rq.force, rq.model),
      invalidatesTags: ['ApprovalProcessConfigs'],
    }),
    putApprovalProcessConfig: builder.mutation<
      ApprovalProcessConfigViewModel,
      { id: string; force?: boolean; model: ApprovalProcessConfigInputModel }
    >({
      query: (rq) => client.putApprovalProcessConfig(rq.id, rq.force, rq.model),
      invalidatesTags: ['ApprovalProcessConfigs'],
    }),
    getWorkflowConfigs: builder.query<WorkflowConfigViewModel[], void>({
      query: () => client.getWorkflowConfigs(),
      providesTags: ['WorkflowConfigs'],
    }),
    getWorkflowConfig: builder.query<WorkflowConfigViewModel, string>({
      query: (id) => client.getWorkflowConfig(id),
      providesTags: ['WorkflowConfigs'],
    }),
    putWorkflowConfig: builder.mutation<WorkflowConfigViewModel, { id: string; model: WorkflowConfigInputModel }>({
      query: ({ id, model }) => client.putWorkflowConfig(id, model),
      invalidatesTags: ['WorkflowConfigs'],
    }),
    postWorkflowConfig: builder.mutation<WorkflowConfigViewModel, WorkflowConfigInputModel>({
      query: (model) => client.postWorkflowConfig(model),
      invalidatesTags: ['WorkflowConfigs'],
    }),
    getOrderSuppliersAudit: builder.query<OrderSuppliersAuditViewModel[], string>({
      query: (id) => client.getOrderSuppliersAudit(id),
      providesTags: ['OrdersSuppliersAudit'],
    }),
    updateOrderSuppliers: builder.mutation<OrderSuppliersViewModel, { id: string; model: OrderSuppliersInputModel }>({
      query: ({ id, model }) => client.putOrderSuppliers(id, model),
      invalidatesTags: ['OrdersInternal', 'OrdersSuppliers', 'OrdersInternalAudit', 'OrdersSuppliersAudit'],
    }),
    getOrderInternal: builder.query<OrderInternalViewModel, { id: string; document?: string }>({
      query: (model) => client.getOrderInternal(model.id, model.document),
      providesTags: ['OrdersInternal'],
    }),
    getOrderInternalAudit: builder.query<OrderInternalAuditViewModel[], string>({
      query: (id) => client.getOrderInternalAudit(id),
      providesTags: ['OrdersInternalAudit'],
    }),
    updateOrderInternal: builder.mutation<OrderInternalViewModel, { id: string; model: OrderInternalInputModel }>({
      query: ({ id, model }) => client.putOrderInternal(id, model),
      invalidatesTags: ['OrdersInternal', 'OrdersSuppliers', 'OrdersInternalAudit', 'OrdersSuppliersAudit'],
    }),
    getWorkflowOrdersSuppliers: builder.query<OrderSuppliersViewModel[], string>({
      query: (configId) => client.getWorkflowOrdersSuppliers(configId),
      providesTags: (res, err, arg) => [{ type: 'WorkflowOrdersSuppliers', id: arg }],
    }),
    getWorkflowOrdersInternal: builder.query<OrderInternalViewModel[], string>({
      query: (configId) => client.getWorkflowOrdersInternal(configId),
      providesTags: (res, err, arg) => [{ type: 'WorkflowOrdersInternal', id: arg }],
    }),
    recreateOrdersInternal: builder.mutation<void, { configId: string; status?: WorkflowOrderStepStatus }>({
      query: (rq) => client.recreateOrdersInternal(rq.configId, rq.status),
      invalidatesTags: (res, err, arg) => [{ type: 'WorkflowOrdersInternal', id: arg.configId }],
    }),
    recreateOrdersSuppliers: builder.mutation<
      void,
      { configId: string; tenantId: string; status?: WorkflowOrderStepStatus }
    >({
      query: (rq) => client.recreateOrdersSuppliers(rq.configId, rq.tenantId, rq.status),
      invalidatesTags: (res, err, arg) => [{ type: 'WorkflowOrdersSuppliers', id: arg.configId }],
    }),
    getOrder: builder.query<OrderViewModel, string>({
      query: (orderId) => client.getOrder(orderId),
      providesTags: ['Orders'],
    }),
    postOrder: builder.mutation<OrderViewModel, { final?: boolean; model: OrderInputModel }>({
      query: ({ final, model }) => client.postOrder(final, model),
      invalidatesTags: ['Orders', 'VehicleOrders'],
    }),
    putOrder: builder.mutation<OrderViewModel, { orderId: string; final: boolean | undefined; model: OrderInputModel }>(
      {
        query: ({ orderId, final, model }) => client.putOrder(orderId, final, model),
        invalidatesTags: ['Orders', 'VehicleOrders'],
      }
    ),
    archiveOrder: builder.mutation<void, string>({
      query: (id) => client.archiveOrder(id),
      invalidatesTags: ['Orders', 'VehicleOrders'],
    }),
    restoreOrderFromArchive: builder.mutation<void, string>({
      query: (id) => client.restoreOrderFromArchive(id),
      invalidatesTags: ['Orders', 'VehicleOrders'],
    }),
    setNextApprovalProcessStep: builder.mutation<
      ApprovalProcessViewModel,
      { processId: string; model: NextApprovalStepInputModel }
    >({
      query: (rq) => client.setNextApprovalProcessStep(rq.processId, rq.model),
      invalidatesTags: ['Orders', 'VehicleOrders'],
    }),
    deleteOrder: builder.mutation<void, string>({
      query: (id) => client.deleteOrder(id),
      invalidatesTags: ['Orders', 'VehicleOrders'],
    }),
    getOrders: builder.query<OrderViewModel[], void>({
      query: () => client.getOrders(),
      providesTags: ['Orders'],
    }),
    searchOrders: builder.query<OrderViewModel[], { dataKey: string; dataValue: string }>({
      query: (model) => client.searchOrders(model.dataKey, model.dataValue),
    }),
    getOrdersForms: builder.query<OrdersFormViewModel[], void>({
      query: () => client.getOrdersForms(),
      providesTags: [{ type: 'OrdersForms', id: 'LIST' }],
    }),
    postOrdersForm: builder.mutation<OrdersFormViewModel, OrdersFormInputModel>({
      query: (model) => client.postOrdersForm(model),
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'OrdersForms', id: 'LIST' },
              { type: 'OrdersForms', id: res.id },
            ]
          : [],
    }),
    putOrdersForm: builder.mutation<OrdersFormViewModel, { id: string; model: OrdersFormInputModel }>({
      query: ({ id, model }) => client.putOrdersForm(id, model),
      invalidatesTags: (res, err, arg) =>
        res
          ? [
              { type: 'OrdersForms', id: 'LIST' },
              { type: 'OrdersForms', id: arg.id },
            ]
          : [],
    }),
    deleteOrdersForm: builder.mutation<void, string>({
      query: (formId) => client.deleteOrdersForm(formId),
      invalidatesTags: (res, err, arg) => [
        { type: 'OrdersForms', id: 'LIST' },
        { type: 'OrdersForms', id: arg },
      ],
    }),
    postWorkItem: builder.mutation<OrderViewModel, OrderInputModel>({
      query: (model) => client.postWorkItem('', model),
    }),
    getOrdersForm: builder.query<OrdersFormViewModel, string>({
      query: (formId) => client.getOrdersForm(formId),
      providesTags: (res, err, arg) => [{ type: 'OrdersForms', id: arg }],
    }),
    getWorkItems: builder.query<OrderViewModel[], void>({
      query: () => client.getWorkItems(),
    }),
    getWorkItemsForms: builder.query<OrdersFormAnonymousViewModel[], void>({
      query: () => client.getWorkItemsForms(),
    }),
    getVehicleOrders: builder.query<VehicleOrderViewModel[], void>({
      query: () => client.getVehicleOrders(),
      providesTags: ['VehicleOrders'],
    }),
    getVehicleReturns: builder.query<VehicleReturnViewModel[], void>({
      query: () => client.getVehicleReturns(),
      providesTags: ['allVehicleReturns'],
    }),
    getVehicleOrder: builder.query<VehicleOrderViewModel, string>({
      query: (orderId) => client.getVehicleOrder(orderId),
    }),
    getVehicleReturn: builder.query<VehicleReturnViewModel, string>({
      query: (orderId) => client.getVehicleReturn(orderId),
    }),
    putVehicleOrder: builder.mutation<VehicleOrderViewModel, { id: string; model: VehicleOrderInputModel }>({
      query: ({ id, model }) => client.putVehicleOrder(id, model),
      invalidatesTags: ['VehicleOrders'],
    }),
    putVehicleReturn: builder.mutation<VehicleReturnViewModel, { orderId: string; model: VehicleReturnInputModel }>({
      query: ({ orderId, model }) => client.putVehicleReturn(orderId, model),
      invalidatesTags: ['Orders', 'allVehicleReturns'],
    }),
    getSupplierUpload: builder.query<SupplierUploadViewModel, string>({
      query: (supplierUploadId) => client.getSupplierUpload(supplierUploadId),
      providesTags: ['SupplierUploads'],
    }),
    getSupplierUploads: builder.query<SupplierUploadViewModel[], void>({
      query: () => client.getSupplierUploads(),
      providesTags: ['SupplierUploads'],
    }),
    postSupplierUpload: builder.mutation<FileResponse, SupplierUploadInputModel>({
      query: (model) => client.postSupplierUpload(model),
      invalidatesTags: [
        'SupplierUploads',
        'OrdersInternal',
        'OrdersSuppliers',
        'OrdersInternalAudit',
        'OrdersSuppliersAudit',
      ],
    }),
    getOrdersApiHealth: builder.query<{ status: 'Healthy' | 'Degraded' | 'Unhealthy' }, void>({
      query: () =>
        fetch(`${Constants.ordersEndpoint}/health`)
          .then((r) => (r.ok ? r.json() : { status: 'Unhealthy' }))
          .then((r) => ({ data: r })),
    }),
    getFuelCards: builder.query<FuelCardViewModel[], void>({
      query: () => client.getFuelCards(),
      providesTags: ['FuelCards'],
    }),
    putFuelCard: builder.mutation<FuelCardViewModel, { id: string; model: FuelCardInputModel }>({
      query: ({ id, model }) => client.putFuelCard(id, model),
      invalidatesTags: ['FuelCards'],
    }),
    postFuelCard: builder.mutation<FuelCardViewModel, FuelCardInputModel>({
      query: (model) => client.postFuelCard(model),
      invalidatesTags: ['FuelCards'],
    }),
    deleteFuelCard: builder.mutation<void, string>({
      query: (id) => client.deleteFuelCard(id),
      invalidatesTags: ['FuelCards'],
    }),
    getTenantFuelCardSets: builder.query<TenantFuelCardSetViewModel[], void>({
      query: () => client.getTenantFuelCardSets(),
      providesTags: ['TenantFuelCardSets'],
    }),
    getTenantFuelCardSet: builder.query<TenantFuelCardSetViewModel, string>({
      query: (id: string) => client.getTenantFuelCardSet(id),
      providesTags: ['TenantFuelCardSets'],
    }),
    getTenantFuelCardSetByTenant: builder.query<TenantFuelCardSetViewModel, string>({
      query: (tenantId: string) => client.getTenantFuelCardSetByTenant(tenantId),
      providesTags: ['TenantFuelCardSets'],
    }),
    postTenantFuelCardSet: builder.mutation<
      TenantFuelCardSetViewModel,
      { final?: boolean; model: TenantFuelCardSetInputModel }
    >({
      query: (rq) => client.postTenantFuelCardSet(rq.final, rq.model),
      invalidatesTags: ['TenantFuelCardSets'],
    }),
    putTenantFuelCardSet: builder.mutation<
      TenantFuelCardSetViewModel,
      { id: string; final?: boolean; model: TenantFuelCardSetInputModel }
    >({
      query: (rq) => client.putTenantFuelCardSet(rq.id, rq.final, rq.model),
      invalidatesTags: ['TenantFuelCardSets'],
    }),
    deleteTenantFuelCardSet: builder.mutation<void, string>({
      query: (id) => client.deleteTenantFuelCardSet(id),
      invalidatesTags: ['TenantFuelCardSets'],
    }),
    getApproverEmails: builder.query<ApproverViewModel[], string>({
      query: (formId) => client.approverEmails(formId),
      providesTags: ['ApproverEmails'],
    }),
    getOrderDocument: builder.query<FileResponse, { orderId: string; documentId: string }>({
      query: (rq) => client.getOrderDocument(rq.orderId, rq.documentId),
    }),
    getOrderFormsStatistics: builder.query<{ [key: string]: number }, void>({
      query: () => client.getOrdersFormsStatistics(),
    }),
  }),
  tagTypes: [
    'OrdersInternal',
    'OrdersSuppliers',
    'OrdersInternalAudit',
    'OrdersSuppliersAudit',
    'SupplierUploads',
    'Orders',
    'VehicleOrders',
    'OrdersForms',
    'allVehicleReturns',
    'WorkflowConfigs',
    'FuelCards',
    'TenantFuelCardSets',
    'ApproverEmails',
    'WorkflowOrdersSuppliers',
    'WorkflowOrdersInternal',
    'ApprovalProcessConfigs',
  ],
})

export function isSupplierOrder(
  order: OrderInternalViewModel | OrderSuppliersViewModel
): order is OrderSuppliersViewModel {
  return 'tenantId' in order && !!order.tenantId
}
export function isInternalOrder(
  order: OrderInternalViewModel | OrderSuppliersViewModel
): order is OrderInternalViewModel {
  return !('tenantId' in order) || !order.tenantId
}

export const {
  useGetOrderSuppliersQuery,
  useLazyGetOrderSuppliersQuery,
  useGetOrderInternalQuery,
  useLazyGetOrderInternalQuery,
  useGetOrderSuppliersAuditQuery,
  useGetOrderInternalAuditQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useSearchOrdersQuery,
  useLazyGetOrdersQuery,
  useGetOrdersFormQuery,
  useGetOrdersFormsQuery,
  useGetWorkItemsQuery,
  useLazyGetWorkItemsFormsQuery,
  useGetVehicleOrderQuery,
  useLazyGetVehicleOrderQuery,
  useGetVehicleOrdersQuery,
  useGetWorkItemsFormsQuery,
  useGetSupplierUploadQuery,
  useLazyGetSupplierUploadQuery,
  useGetSupplierUploadsQuery,
  useUpdateOrderSuppliersMutation,
  useUpdateOrderInternalMutation,
  usePostSupplierUploadMutation,
  usePostWorkItemMutation,
  usePutOrderMutation,
  usePostOrderMutation,
  useArchiveOrderMutation,
  useRestoreOrderFromArchiveMutation,
  useDeleteOrderMutation,
  usePutVehicleOrderMutation,
  useGetVehicleReturnsQuery,
  useLazyGetVehicleReturnsQuery,
  usePutVehicleReturnMutation,
  useGetVehicleReturnQuery,
  useGetOrdersQuery,
  usePutOrdersFormMutation,
  usePostOrdersFormMutation,
  useDeleteOrdersFormMutation,
  useGetApprovalProcessConfigsQuery,
  useGetWorkflowConfigsQuery,
  useGetWorkflowConfigQuery,
  usePostWorkflowConfigMutation,
  usePutWorkflowConfigMutation,
  useGetOrdersApiHealthQuery,
  useGetFuelCardsQuery,
  usePutFuelCardMutation,
  usePostFuelCardMutation,
  useDeleteFuelCardMutation,
  useGetApproverEmailsQuery,
  useLazyGetOrderDocumentQuery,
  useGetOrderFormsStatisticsQuery,
} = ordersApi
